import { getComboSubSelections } from "@/utils/comboSub.utils";
import { checkCouponNormalizedOrder, compareCouponIds, createCouponContractError, getCouponCalculation } from "@/utils/couponCalculation.utils";
import { getFrontEndName } from "@/utils/productName.utils";
import { getCouponValidFromAt, getCouponValidUntilAt, isCouponExpired } from "@/utils/coupon.utils";

// POS 的 .round() 為負值 half away from zero；不可直接以 Math.round(-0.5) 映射。
function roundCouponAmount(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) throw createCouponContractError();
  return Math.sign(value) * Math.round(Math.abs(value));
}

function getSpecOptions(settings, componentPath) {
  return (settings || []).flatMap((spec) => (spec.subSpecOptionSelectCount || []).flatMap((count, index) => {
    if (!Number.isInteger(count) || count < 0) throw createCouponContractError();
    if (!count) return [];
    const option = spec.specOptions?.[index];
    if (!option) throw createCouponContractError();
    return [{
      spec_id: spec.id || spec.subSpecID,
      option_id: option.id,
      ...(componentPath ? { component_path: componentPath } : {}),
      unit_surcharge_amount: roundCouponAmount(option.price * count),
    }];
  }));
}

// 試算用的穩定行號；建單時改用 OrderGoods 的 id（總折抵不受行號影響）。
const getPreviewLineId = (index) => `line-${String(index + 1).padStart(3, "0")}`;

export function getCouponNormalizedOrder({ cart, lineIds, fees = {} }) {
  const items = cart.map((item, index) => {
    const line = {
      order_line_id: lineIds ? lineIds[index] : getPreviewLineId(index),
      root_type: item.isCombo ? "combo" : "product",
      root_id: item.product?.id,
      quantity: item.quantity,
      // 兌換券自動加入的行優先被兌換
      ...(item.couponExchange ? { coupon_benefit: true } : {}),
    };
    let unitAmount = item.product?.price;
    let options;
    if (!item.isCombo) options = getSpecOptions(item.settings);
    else {
      const components = [];
      options = [];
      const addChild = (product, slot) => {
        const path = [slot, product.id];
        components.push({ component_path: path, product_id: product.id });
        options.push(...getSpecOptions(product.productSpecSettings, path));
      };
      (item.fixedProducts || []).forEach((p) => addChild(p, "combo_fixed"));
      for (const group of item.comboSubItem || []) {
        const slot = group.comboSub?.id || group.subSpecID || group.id || group.frontEndDisplayName;
        for (const { product, count } of getComboSubSelections(group)) {
          // 後端 components 沒有數量，子項多份無法表示（待決策 D2）。
          if (count !== 1) throw createCouponContractError();
          // 選配加價計入套餐本體（D1 已確認），與 cart/productUnitPrice 一致。
          unitAmount += product.altAddonPrice || 0;
          addChild(product, slot);
        }
      }
      (item.comboSubAnyProducts?.orderProducts || []).forEach((p) => addChild(p, "combo_any"));
      if (components.length) line.components = components;
    }
    line.unit_merchandise_amount = roundCouponAmount(unitAmount);
    if (options.length) line.spec_options = options;
    return line;
  }).sort((a, b) => compareCouponIds(a.order_line_id, b.order_line_id));
  return checkCouponNormalizedOrder({ items, fees: {
    service_fee: fees.service_fee ?? 0,
    delivery_fee: fees.delivery_fee ?? 0,
    card_fee: fees.card_fee ?? 0,
    other_fee: fees.other_fee ?? 0,
  } });
}

function getCouponPayRateText(payRatePercent) {
  const [whole, decimal = ""] = String(payRatePercent).split(".");
  const digits = whole.padStart(2, "0");
  return `${digits.slice(0, -1)}.${digits.slice(-1)}${decimal}`.replace(/0+$/, "").replace(/\.$/, "");
}

export function getCouponBenefitSummary(rules) {
  const targetType = rules.benefit_targets?.[0]?.target_type === "combo" ? "套餐" : "商品";
  if (rules.benefit_type === "target_exchange") return `兌換指定${targetType} ${rules.benefit_quantity} 份`;
  if (rules.benefit_scope === "order") {
    return rules.benefit_type === "percentage" ? `整單 ${getCouponPayRateText(rules.pay_rate_percent)} 折`
      : `整單折抵 $${rules.order_discount_amount}`;
  }
  const benefit = rules.benefit_type === "percentage" ? ` ${getCouponPayRateText(rules.pay_rate_percent)} 折`
    : `每份折抵 $${rules.target_unit_discount_amount}`;
  return `指定${targetType}${benefit}，最多 ${rules.benefit_quantity} 份`;
}

export function getCouponUnavailableReasonTexts({ currentPublicRules, unmetConditions = [], isApplicable }) {
  if (isApplicable) return [];
  const messages = {
    MIN_ORDER_AMOUNT_NOT_MET: `消費滿 $${currentPublicRules.min_order_amount} 可使用`,
    REQUIRED_TARGET_QUANTITY_NOT_MET: "尚未符合指定商品或規格條件",
    BENEFIT_TARGET_NOT_IN_ORDER: currentPublicRules.benefit_type === "target_exchange"
      ? "購物車需包含指定兌換商品" : "購物車需包含指定優惠商品",
    ORDER_ITEMS_EMPTY: "此優惠券不適用目前訂單",
  };
  const reasons = Object.keys(messages).filter((code) => unmetConditions.includes(code)).map((code) => messages[code]);
  return reasons.length ? [...new Set(reasons)] : ["此優惠券不適用目前訂單"];
}

const getTargetName = (value) => typeof value === "string" ? value.trim() : "";

export function getCouponTargetText({ target }) {
  const targetName = getTargetName(target.target_name);
  if (target.target_type !== "spec_option") {
    return getFrontEndName(targetName) || (target.target_type === "combo" ? "指定套餐" : "指定商品");
  }
  const rootName = getFrontEndName(getTargetName(target.root_name)) || (target.root_type === "combo" ? "指定套餐" : "指定商品");
  const specName = getTargetName(target.spec_name);
  if (!specName || !targetName) return `${rootName}／指定規格選項`;
  return `${rootName}需選擇${specName}：${targetName}`;
}

const UNSUPPORTED_TEXT = "此訂單暫不支援使用此優惠券";

// 選券狀態由購物車即時計算，不保存折抵金額；舊格式（無 memberCouponUuid）視為未選。
export function getCouponApplication({ selectedCoupon, cart, storeMemberUuid }) {
  if (!selectedCoupon?.memberCouponUuid || !storeMemberUuid ||
      selectedCoupon.storeMemberUuid !== storeMemberUuid) {
    return { status: "none", discountAmount: 0, unmetTexts: [] };
  }
  const rules = selectedCoupon.currentPublicRules;
  if (!Array.isArray(cart) || !cart.length) {
    return { status: "unmet", discountAmount: 0, unmetTexts: getCouponUnavailableReasonTexts({ currentPublicRules: rules || {}, isApplicable: false }) };
  }
  let calculation;
  try {
    calculation = getCouponCalculation({ currentPublicRules: rules, normalizedOrder: getCouponNormalizedOrder({ cart }) });
  } catch (error) {
    return { status: "unsupported", discountAmount: 0, unmetTexts: [UNSUPPORTED_TEXT] };
  }
  if (calculation.isApplicable) {
    return { status: "applied", discountAmount: calculation.couponDiscountAmount, unmetTexts: [] };
  }
  return { status: "unmet", discountAmount: 0,
    unmetTexts: getCouponUnavailableReasonTexts({ currentPublicRules: rules, ...calculation }) };
}

// validate 回應轉成 cart.selectedCoupon；版本標記不決定可用性。
export function getSelectedCouponFromValidation({ data, memberCouponUuid, storeMemberUuid, fallbackTitle = "" }) {
  const nowMs = Date.now();
  const validFromMs = Date.parse(getCouponValidFromAt(data));
  if (!data || data.member_coupon_uuid !== memberCouponUuid ||
      data.coupon_status !== "available" || (data.effective_status != null && data.effective_status !== "available") ||
      isCouponExpired(data, nowMs) || (Number.isFinite(validFromMs) && nowMs < validFromMs) ||
      !Number.isSafeInteger(data.validated_revision_no) || !data.current_public_rules ||
      typeof data.current_public_rules !== "object") {
    throw createCouponContractError();
  }
  return {
    memberCouponUuid,
    storeMemberUuid,
    title: data.current_public_rules.title || fallbackTitle || "優惠券",
    validatedRevisionNo: data.validated_revision_no,
    currentPublicRules: data.current_public_rules,
    validUntilAt: typeof getCouponValidUntilAt(data) === "string" ? getCouponValidUntilAt(data) : null,
  };
}

// 建單時以 OrderGoods 的 id 重算，產生要多帶的契約欄位。
export function buildCouponOrderPayload({ selectedCoupon, cart, lineIds, fees }) {
  const normalizedOrder = getCouponNormalizedOrder({ cart, lineIds, fees });
  const calculation = getCouponCalculation({ currentPublicRules: selectedCoupon.currentPublicRules, normalizedOrder });
  if (!calculation.isApplicable) throw new Error("優惠金額已變更，請重新確認");
  return {
    normalizedOrder,
    couponDiscountAmount: calculation.couponDiscountAmount,
    orderFields: {
      member_coupon_uuid: selectedCoupon.memberCouponUuid,
      validated_revision_no: selectedCoupon.validatedRevisionNo,
      coupon_discount_amount: calculation.couponDiscountAmount,
      actual_benefit_items: calculation.actualBenefitItems,
    },
  };
}

const ORDER_COUPON_CLEAR_CODES = ["COUPON_MEMBER_COUPON_NOT_AVAILABLE", "COUPON_MEMBER_COUPON_NOT_FOUND",
  "COUPON_TEMPLATE_NOT_ACTIVE", "COUPON_MEMBER_STORE_SCOPE_INVALID"];

// 用券建單失敗（legacy envelope）轉成帶 couponAction 的錯誤：clear 清除選券、keep 保留待重驗。
export function getCouponOrderError(data) {
  const code = data?.coupon_error_code;
  let message;
  let couponAction = "keep";
  if (ORDER_COUPON_CLEAR_CODES.includes(code)) {
    message = "此優惠券目前無法使用，已取消套用，請確認金額後重新結帳";
    couponAction = "clear";
  } else if (code === "COUPON_NO_EFFECTIVE_BENEFIT") {
    message = "此優惠券不適用目前訂單，已取消套用";
    couponAction = "clear";
  } else if (["COUPON_TEMPLATE_REVISION_CONFLICT", "COUPON_ORDER_PRICE_MISMATCH"].includes(code)) {
    message = "優惠內容已更新，請確認金額後重新結帳";
  } else {
    message = data?.err_msg || "優惠券暫時無法使用，請取消使用後再結帳";
  }
  const error = new Error(message);
  error.code = code || "ORDER_CREATE_FAILED";
  error.couponAction = couponAction;
  return error;
}
